import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

const CmsContext = createContext({
    settings: {},
    pages: {},
    lists: {},
    loaded: false,
    refresh: () => {},
});

/**
 * Provides ALL editable content (settings, pages, lists) in one shot via /api/cms/bootstrap.
 * Public site uses this so admin edits propagate everywhere on the next refresh.
 */
export function CmsProvider({ children }) {
    const [data, setData] = useState({ settings: {}, pages: {}, lists: {}, loaded: false });

    const refresh = useCallback(async () => {
        try {
            const res = await api.get("/cms/bootstrap");
            setData({ ...res.data, loaded: true });
        } catch (e) {
            // Don't break the site — leave defaults from page-level fallbacks
            console.warn("CMS bootstrap failed:", e?.message);
            setData((prev) => ({ ...prev, loaded: true }));
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const value = useMemo(() => ({ ...data, refresh }), [data, refresh]);
    return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms() {
    return useContext(CmsContext);
}

/**
 * Hook scoped to a single page.
 * Returns a getter `t(section, key, fallback)` that resolves to the stored value
 * or the provided fallback (so the site renders correctly while CMS is loading
 * or if a value was never set).
 */
export function usePageContent(pageId) {
    const { pages, loaded } = useCms();
    const { en } = useLocale();
    const pageData = useMemo(() => pages?.[pageId] || {}, [pages, pageId]);

    const t = useCallback(
        (section, key, fallback = "") => {
            // In EN mode fallbacks are already localized in code — CMS stores PT only
            if (en && typeof fallback === "string" && fallback !== "" && !/^(\/|https?:)/.test(fallback)) return fallback;
            const v = pageData?.[section]?.[key];
            return v != null && v !== "" ? v : fallback;
        },
        [pageData, en]
    );

    return { t, loaded, raw: pageData };
}

/**
 * Returns array of items for a given list id, falling back to provided defaults.
 */
export function useContentList(listId, fallback = [], { forceFallbackEn = true } = {}) {
    const { lists, loaded } = useCms();
    const { en } = useLocale();
    const items = lists?.[listId];
    if (en && forceFallbackEn && fallback.length > 0) return fallback;
    if (!loaded || !items || items.length === 0) return fallback;
    return items;
}

/**
 * Returns global site setting value with fallback.
 */
export function useSetting(key, fallback = "", { localized = false } = {}) {
    const { settings } = useCms();
    const { en } = useLocale();
    if (en && localized && fallback !== "") return fallback;
    return settings?.[key] ?? fallback;
}
